import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import './Tabs.scss';

const Tabs = ({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  variant = 'underline',
  size = 'medium',
  persistInUrl = true,
  urlParamName = 'tab',
  enableCarousel = true,
  autoScrollToActive = true,
  preserveScrollPosition = false
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialized = useRef(false);
  const scrollPositionRef = useRef(0);

  // Embla carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
    skipSnaps: true
  });

  // Effect to preserve scroll position
  useEffect(() => {
    if (!emblaApi || !enableCarousel || !preserveScrollPosition) return;

    const onScroll = () => {
      scrollPositionRef.current = emblaApi.scrollProgress();
    };

    emblaApi.on('scroll', onScroll);
    return () => emblaApi.off('scroll', onScroll);
  }, [emblaApi, enableCarousel, preserveScrollPosition]);

  // Fonction pour obtenir le tab depuis l'URL
  const getTabFromUrl = useCallback(() => {
    if (!persistInUrl) return null;
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get(urlParamName);
  }, [persistInUrl, location.search, urlParamName]);

  // Fonction pour mettre à jour l'URL avec le tab actif
  const updateUrlWithTab = useCallback((tabId) => {
    if (!persistInUrl) return;
    const urlParams = new URLSearchParams(location.search);
    const currentUrlTab = urlParams.get(urlParamName);

    // Éviter les navigations inutiles
    if (currentUrlTab === tabId) return;

    urlParams.set(urlParamName, tabId);
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
  }, [persistInUrl, location.search, location.pathname, urlParamName, navigate]);

  // Filter out null values from tabs array
  const filteredTabs = tabs ? tabs.filter(tab => tab !== null && tab !== undefined) : [];

  // Déterminer le tab qui devrait être actif
  const getEffectiveActiveTab = useCallback(() => {
    // 1. Si activeTab est fourni (composant contrôlé), l'utiliser
    if (activeTab !== undefined) {
      return activeTab;
    }

    // 2. Si pas de tabs disponibles, retourner null
    if (!filteredTabs || filteredTabs.length === 0) {
      return null;
    }

    // 3. Si persistInUrl est activé, vérifier l'URL
    if (persistInUrl) {
      const urlTab = getTabFromUrl();
      if (urlTab && filteredTabs.some(tab => tab.id === urlTab)) {
        return urlTab;
      }
    }

    // 4. Par défaut, premier tab
    return filteredTabs[0]?.id || null;
  }, [activeTab, filteredTabs, persistInUrl, getTabFromUrl]);

  const [internalActiveTab, setInternalActiveTab] = useState(null);

  // Effet principal : synchroniser l'état avec les props, URL et tabs
  useEffect(() => {
    const effectiveTab = getEffectiveActiveTab();

    // Si on n'a pas encore initialisé ou si le tab a changé
    if (!isInitialized.current || internalActiveTab !== effectiveTab) {
      setInternalActiveTab(effectiveTab);
      isInitialized.current = true;

      // Si composant non contrôlé et persistInUrl activé, synchroniser l'URL
      if (activeTab === undefined && persistInUrl && effectiveTab) {
        updateUrlWithTab(effectiveTab);
      }
    }
  }, [getEffectiveActiveTab, internalActiveTab, activeTab, persistInUrl, updateUrlWithTab]);

  const handleTabClick = (tabId) => {
    // Mettre à jour l'état interne si composant non contrôlé
    if (activeTab === undefined) {
      setInternalActiveTab(tabId);
    }

    // Mettre à jour l'URL si persistInUrl est activé
    if (persistInUrl) {
      updateUrlWithTab(tabId);
    }

    // Notifier le parent
    onTabChange?.(tabId);
  };

  const currentActiveTab = activeTab !== undefined ? activeTab : internalActiveTab;

  // Effect to restore scroll position when preserveScrollPosition is true
  useEffect(() => {
    if (!emblaApi || !enableCarousel || !preserveScrollPosition) return;

    const timeoutId = setTimeout(() => {
      if (scrollPositionRef.current > 0) {
        emblaApi.scrollTo(scrollPositionRef.current * (emblaApi.scrollSnapList().length - 1));
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentActiveTab, emblaApi, enableCarousel, preserveScrollPosition]);

  // Effect to scroll active tab into view when autoScrollToActive is true
  useEffect(() => {
    if (!emblaApi || !enableCarousel || !currentActiveTab || !autoScrollToActive || preserveScrollPosition) return;

    const activeTabIndex = filteredTabs.findIndex(tab => tab.id === currentActiveTab);
    if (activeTabIndex === -1) return;

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      // Check if the active tab is visible in the current viewport
      const slides = emblaApi.slideNodes();
      const activeSlide = slides[activeTabIndex];

      if (activeSlide) {
        const containerRect = emblaApi.containerNode().getBoundingClientRect();
        const slideRect = activeSlide.getBoundingClientRect();

        // If the slide is not fully visible, scroll to it
        if (slideRect.left < containerRect.left || slideRect.right > containerRect.right) {
          emblaApi.scrollTo(activeTabIndex);
        }
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentActiveTab, emblaApi, enableCarousel, filteredTabs, autoScrollToActive, preserveScrollPosition]);

  return (
    <div className={`tabs ${variant} ${size} ${enableCarousel ? 'carousel-enabled' : ''} ${className}`}>
      {enableCarousel ? (
        <div className="tabs-header embla" ref={emblaRef}>
          <div className="embla__container">
            {filteredTabs.map((tab) => (
              <div key={tab.id} className="embla__slide">
                <div
                  className={`tab-button ${currentActiveTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
                  onClick={() => !tab.disabled && handleTabClick(tab.id)}
                  disabled={tab.disabled}
                  aria-selected={currentActiveTab === tab.id}
                  role="tab"
                >
                  {tab.icon && <span className="tab-icon">{tab.icon}</span>}
                  <span className="tab-label">{tab.label}</span>
                  {tab.badge && <span className="tab-badge">{tab.badge}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="tabs-header">
          {filteredTabs.map((tab) => (
            <div
              key={tab.id}
              className={`tab-button ${currentActiveTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
              onClick={() => !tab.disabled && handleTabClick(tab.id)}
              disabled={tab.disabled}
              aria-selected={currentActiveTab === tab.id}
              role="tab"
            >
              {tab.icon && <span className="tab-icon">{tab.icon}</span>}
              <span className="tab-label">{tab.label}</span>
              {tab.badge && <span className="tab-badge">{tab.badge}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="tabs-content">
        {filteredTabs.map((tab) => (
          <div
            key={tab.id}
            className={`tab-panel ${currentActiveTab === tab.id ? 'active' : ''}`}
            role="tabpanel"
            aria-hidden={currentActiveTab !== tab.id}
          >
            {currentActiveTab === tab.id && tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node,
      icon: PropTypes.node,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['modern', 'pills', 'underline', 'minimal']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  persistInUrl: PropTypes.bool,
  urlParamName: PropTypes.string,
  enableCarousel: PropTypes.bool,
  autoScrollToActive: PropTypes.bool,
  preserveScrollPosition: PropTypes.bool,
};

export default Tabs;